import type { WithActiveBranchId } from '@app/api/shared';

// CRM.Dots/SocialIntegrations/LoyaltyChat/RecommendCrmContracts.cs (US 11099).
// The enclosing legacy CRM DTO keeps PascalCase; RecommendChat uses camelCase.
export enum RecommendChatEndpoints {
  GetSettings = 'GetRecommendChatSettings',
  SaveSettings = 'SaveRecommendChatSettings',
  AcknowledgeMessages = 'AcknowledgeRecommendChatMessages',
  GetBindingConflicts = 'GetRecommendChatBindingConflicts',
  ResolveBinding = 'ResolveRecommendChatBinding',
}

export interface RecommendChatCapability {
  available: boolean;
  reasonCode: string | null;
  conversationId: string | null;
  textOnly: boolean;
  maxTextLength: number;
}

export type RecommendMessageOrigin = 'client' | 'staff' | 'system' | 'sendout';
export type RecommendRecipientState =
  | 'pending'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'failed';

export interface RecommendHistoryMetadata {
  conversationId: string;
  messageId: string;
  origin: RecommendMessageOrigin;
  sequence: string;
  revision: string;
  recipientState: RecommendRecipientState;
  author: {
    type: 'client' | 'staff' | 'system';
    displayName: string;
  };
  staffReadPending: boolean;
  readOperationId: string | null;
  readOperationStatus: 'pending' | 'applied' | 'rejected' | null;
  readOperationErrorCode: string | null;
  updateKind: 'incoming' | 'update';
}

export interface RecommendLeadEvent {
  eventId: string;
  messageId: string;
  revision: string;
  kind: 'incoming' | 'update';
}

export interface RecommendNamedItem {
  id: number;
  name: string;
}

export interface RecommendChatSettingsValues {
  integrationId: number;
  chatEnabled: boolean;
  operatorUserIds: number[];
  configurationVersion: string;
}

export interface RecommendChatSettings extends RecommendChatSettingsValues {
  options: {
    operators: RecommendNamedItem[];
    canResolveBindings: boolean;
  };
}

export interface RecommendReadOperation {
  conversationId: string;
  messageIds: string[];
  operationId: string;
}

export interface RecommendAcknowledgeMessagesResult
  extends RecommendReadOperation {
  pendingSync: boolean;
}

export interface RecommendBindingCandidate {
  clientId: number;
  leadId: number | null;
  branchId: number;
  name: string;
  branchName: string;
}

export interface RecommendBindingConflict {
  conversationId: string;
  appClientUuid: string;
  bindingRevision: string;
  displayName: string;
  reasonCode: string;
  candidates: RecommendBindingCandidate[];
  createdAt: string;
}

export interface RecommendResolveBindingValues {
  conversationId: string;
  bindingRevision: string;
  operation: 'linkExisting';
  clientId: number;
  leadId: number | null;
  reason: string;
}

export interface RecommendResolveBindingResult {
  conversationId: string;
  bindingRevision: string;
  status: 'READY' | 'CONFLICT' | 'PENDING';
}

export type GetRecommendChatSettingsRequest = WithActiveBranchId<{
  integrationId: number;
}>;
export type SaveRecommendChatSettingsRequest = WithActiveBranchId<{
  data: RecommendChatSettingsValues;
}>;
export type AcknowledgeRecommendChatMessagesRequest = WithActiveBranchId<{
  data: RecommendReadOperation;
}>;
export type GetRecommendChatBindingConflictsRequest =
  GetRecommendChatSettingsRequest;
export type ResolveRecommendChatBindingRequest = WithActiveBranchId<{
  data: RecommendResolveBindingValues;
}>;
